import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";
import type { Database } from "../lib/database.types";
import type { Match } from "../types";

type MatchRow = Database["public"]["Tables"]["matches"]["Row"];
type CountryTranslationRow =
  Database["public"]["Tables"]["country_translations"]["Row"];

interface MatchWithTranslations extends MatchRow {
  home_country: CountryTranslationRow | null;
  away_country: CountryTranslationRow | null;
}

const mapMatchFromDB = (
  match: MatchWithTranslations
): Match => {
  // Store country codes instead of translated names
  // Components will translate dynamically using useCountryTranslations
  return {
    id: match.id,
    homeTeam: match.home_country_code,
    awayTeam: match.away_country_code,
    homeFlag: match.home_flag,
    awayFlag: match.away_flag,
    matchTime: match.match_time,
    status: match.status,
  };
};

export function useMatches(userId?: string) {
  const [matches, setMatches] = useState<Match[]>([]);
  const [matchesWithPredictions, setMatchesWithPredictions] = useState<
    Set<string>
  >(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchMatches = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current date/time in the same format as stored in DB (YYYY-MM-DD HH:mm)
      const now = new Date();
      const nowString = now.toISOString().slice(0, 16).replace("T", " ");

      // Fetch matches with country translations joined
      const { data, error: fetchError } = await supabase
        .from("matches")
        .select(
          `
          *,
          home_country:country_translations!matches_home_country_code_fkey(*),
          away_country:country_translations!matches_away_country_code_fkey(*)
        `
        )
        .in("status", ["upcoming", "live"])
        .order("match_time", { ascending: true });

      if (fetchError) throw fetchError;

      // Filter: show live matches OR upcoming matches with time >= now
      const filtered = (data || []).filter(
        (match) => match.status === "live" || match.match_time >= nowString
      );

      const mappedMatches = filtered.map((match) =>
        mapMatchFromDB(match as MatchWithTranslations)
      );
      setMatches(mappedMatches);

      // If userId is provided, check which matches have predictions
      if (userId && mappedMatches.length > 0) {
        const matchIds = mappedMatches.map((m) => m.id);
        const { data: predictions } = await supabase
          .from("predictions")
          .select("match_id")
          .eq("user_id", userId)
          .in("match_id", matchIds);

        if (predictions) {
          const predictionMatchIds = new Set(
            predictions.map((p) => p.match_id)
          );
          setMatchesWithPredictions(predictionMatchIds);
        }
      }
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error("Failed to fetch matches");
      setError(error);

    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchMatches();

    // Subscribe to real-time updates
    const channel = supabase
      .channel("matches-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "matches",
        },
        () => {
          fetchMatches();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchMatches]);

  return {
    matches,
    matchesWithPredictions,
    loading,
    error,
    refetch: fetchMatches,
  };
}
